<script lang="ts">
import {
  defineComponent,
  ref,
  watchEffect,
  onMounted,
  computed,
  watch,
  Ref,
  unref,
} from 'vue'
import {
  Model,
  Snake,
  Direction,
  keyToDirection,
  directionToString,
  allColors,
} from '../server-src/model'
import Intro from './components/Intro.vue'
import Leaderboard from './components/Leaderboard.vue'
import { throttle } from 'lodash-es'
import { Camera, rerender } from './renderCanvas'
import { useSwipes } from './useSwipes'
import { generateCanvases } from './renderCanvas'
import { StateGetter } from './StateGetter'
import { io } from 'socket.io-client'

// declare const __DEV__: boolean

const serverUrl: string =
  // @ts-ignore
  import.meta.env.VITE_SERVER_URL ?? '/'

// const socket = io({ port: '3000' })
const socket = io(serverUrl, { transports: ['websocket'], upgrade: false })

const spectating = window.location.href.includes('spectate')

window.addEventListener('beforeunload', () => {
  console.log('closing now.....')
  socket.emit('closing page')
})
const showAll = ref(spectating)

export default defineComponent({
  name: 'App',
  components: { Intro, Leaderboard },
  setup() {
    const started = ref(spectating)

    const width = ref(0)
    const height = ref(0)
    function resize() {
      width.value = Math.max(
        document.documentElement.clientWidth,
        document.body.clientWidth
      )
      height.value = Math.max(
        document.documentElement.clientHeight,
        document.body.clientHeight
      )
    }
    resize()
    window.addEventListener('resize', resize)

    const stateGetter = new StateGetter(socket)
    const model = ref<Model | null>(null)

    function join({
      color,
      name,
    }: {
      color: string
      name: string | Ref<string>
    }) {
      dirs = []
      socket.emit('join', localStorage.getItem('snakeId'), color, unref(name))
      started.value = true
    }
    socket.on('your id', (id: string) => {
      localStorage.setItem('snakeId', id)
    })

    const camera: Ref<Camera> = ref({
      width: width.value,
      height: height.value,
      x: 0,
      y: 0,
      showAll: computed(() => showAll.value),
    })
    watch([model, width, height], () => {
      const headCoord = model.value?.snakes.find(
        (a) => a.id === localStorage.getItem('snakeId')
      )?.head
      camera.value.width = width.value
      camera.value.height = height.value
      if (headCoord) {
        camera.value.x = headCoord[0]
        camera.value.y = headCoord[1]
      }
    })

    const canvas = ref<null | HTMLCanvasElement>(null)
    const emitDirs = throttle(() => {
      dirs.forEach((d) => socket.emit('turn', d))
    }, 20)
    onMounted(() => {
      const ctx = canvas.value!.getContext('2d')!
      let id: number | undefined
      function update() {
        const state = stateGetter.currentState
        model.value = state
        // this looks like it shouldn't use requestAnimationFrame but it should
        if (state != null) {
          requestAnimationFrame(() => rerender(ctx, state, camera.value))
        }
        if (started.value || spectating) {
          requestAnimationFrame(update)
        } else {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              requestAnimationFrame(update)
            })
          })
        }
        emitDirs()
      }
      requestAnimationFrame(update)
    })

    let dirs: Direction[] = []
    document.addEventListener('keydown', (e) => {
      if (!e.repeat) {
        const d = keyToDirection(e.key)
        if (d != null) dirs.push(d)
      }
    })
    const container = ref<null | HTMLDivElement>(null)
    const showCanvas = ref(true)
    document.addEventListener('keyup', (e) => {
      if (started.value) {
        const d = keyToDirection(e.key)
        if (d != null) {
          dirs = dirs.filter((s) => s !== d)
        }
        if (e.key === 'x') {
          dirs = []
        } else if (e.key === 'f') {
          container.value?.requestFullscreen()
        } else if (e.key === 'b') {
          showAll.value = !showAll.value
        } else if (e.key === 'p') {
          showCanvas.value = false
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              resize()
            })
          })
        }
      }
    })

    const mySnake: Ref<Snake | undefined> = computed(() =>
      model.value?.snakes?.find((s) => s.id === localStorage.getItem('snakeId'))
    )
    const currentDirection: Ref<Direction | undefined> = computed(() => {
      const snake = mySnake.value
      if (!snake) return undefined
      if (snake.jointLengths.length === 0) return undefined
      const currentDirectionVertical = snake.initialDirectionVertical
      const lastDirSign = Math.sign(
        snake.jointLengths[snake.jointLengths.length - 1]
      )
      return (currentDirectionVertical ? 2 : 1) * -lastDirSign
    })
    watchEffect(() =>
      console.log(
        'Current direction: ',
        directionToString(currentDirection.value)
      )
    )
    useSwipes(container, started, currentDirection, (dir) =>
      socket.emit('turn', dir)
    )

    const dead = computed(
      () =>
        !model.value?.snakes?.some(
          (s) => s.id === localStorage.getItem('snakeId')
        )
    )
    watch(dead, (deadNow) => {
      if (deadNow)
        setTimeout(() => {
          started.value = false
        }, 2000)
    })

    watchEffect(() => {
      generateCanvases(width.value, height.value, showAll.value)
    })

    const lengthLeaderboard = computed(() =>
      model.value?.snakes
        ?.toSorted((s1, s2) => s2.length - s1.length)
        ?.map((s) => ({ ...s, number: s.length }))
    )
    const timeLeaderboard = computed(() =>
      model.value?.snakes
        ?.toSorted((s1, s2) => s1.spawnTime - s2.spawnTime)
        ?.map((s) => ({ ...s }))
    )
    const killsLeaderboard = computed(() =>
      model.value?.snakes
        ?.toSorted((s1, s2) => s2.kills - s1.kills)
        ?.map((s) => ({ ...s, number: s.kills }))
    )

    return {
      width,
      height,
      canvas,
      model,
      container,
      started,
      join,
      showCanvas,
      lengthLeaderboard,
      timeLeaderboard,
      killsLeaderboard,
    }
  },
  computed: {
    colors(): { color: string; taken: boolean }[] | null {
      return (
        this.model &&
        allColors.map((color) => ({
          color,
          taken: this.model!.snakes.some((snake) => snake.color === color),
        }))
      )
    },
  },
})
</script>

<template>
  <div ref="container" class="container">
    <canvas v-show="showCanvas" ref="canvas" :width="width" :height="height" />
    <Leaderboard
      :leaderboard="lengthLeaderboard"
      style="top: 2rem; left: 1rem"
    />
    <Leaderboard
      :leaderboard="timeLeaderboard"
      style="top: 12rem; left: 1rem"
    />
    <Leaderboard
      :leaderboard="killsLeaderboard"
      style="top: 22rem; left: 1rem"
    />
    <Intro :colors="colors" @join="join" v-if="!started" class="intro" />
  </div>
</template>

<style>
canvas {
  /* border: 1px solid blue; */
  margin: 0;
  padding: 0;
  touch-action: none;
}

html,
body {
  /* overflow: hidden; */
  margin: 0;
  padding: 0;
}

div {
  background-color: white;
}

.intro,
canvas {
  position: absolute;
}

.intro {
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border: 1px solid blue;
  padding: 30px;
  width: 50vw;
  height: 75vh;
}

/* html,
body {
  overflow: hidden;
} */

/* body {
  position: fixed;
} */
</style>
